import { useEffect, useState } from 'react';
import { AddressApi, MetaApi } from '../api';
import { useAuth } from '../context/AuthContext';

const BLANK = {
  fullName: '', phone: '', line1: '', line2: '',
  city: '', state: '', pincode: '', type: 'home', isDefault: false,
};

const PRESET_TYPES = [
  { id: 'home', label: 'Home', icon: 'fa-house' },
  { id: 'work', label: 'Work', icon: 'fa-briefcase' },
  { id: 'other', label: 'Other', icon: 'fa-location-dot' },
];

export default function AddressForm({ address, onClose, onSaved }) {
  const { user } = useAuth();
  const [form, setForm] = useState({ ...BLANK, ...(address || {}) });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const isEdit = Boolean(address?._id);

  // A saved address with a label of its own starts in "custom" mode.
  const [custom, setCustom] = useState(
    Boolean(address?.type) && !PRESET_TYPES.some((t) => t.id === address.type)
  );

  // Prefill name/phone from the logged-in user on a new address.
  useEffect(() => {
    if (!isEdit && user) {
      setForm((f) => ({
        ...f,
        fullName: f.fullName || user.name || '',
        phone: f.phone || user.phone || '',
      }));
    }
  }, [user, isEdit]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Pincode → city + state, so the shopper doesn't have to type them.
  const onPincode = async (e) => {
    const pin = e.target.value.replace(/\D/g, '').slice(0, 6);
    setForm((f) => ({ ...f, pincode: pin }));
    if (pin.length !== 6) return;
    try {
      const info = await MetaApi.pincode(pin);
      setForm((f) => ({
        ...f,
        city: info.district || info.city || f.city,
        state: info.state || f.state,
      }));
    } catch (_) { /* leave the fields for manual entry */ }
  };

  const save = async () => {
    setErr('');
    if (!form.fullName.trim()) return setErr('Please enter a name');
    if (!/^\d{10}$/.test(form.phone)) return setErr('Enter a valid 10-digit phone number');
    if (!form.line1.trim()) return setErr('Please enter the address');
    if (!/^\d{6}$/.test(form.pincode)) return setErr('Enter a valid 6-digit pincode');
    if (!form.type.trim()) return setErr('Please name this address type');

    setBusy(true);
    try {
      const res = isEdit
        ? await AddressApi.update(address._id, form)
        : await AddressApi.create(form);
      onSaved(res.address);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose} aria-label="Close"><i className="fas fa-xmark" /></button>
        <h3>{isEdit ? 'Edit Address' : 'Add New Address'}</h3>

        <div className="form-row">
          <div className="field">
            <label>Full Name *</label>
            <input value={form.fullName} onChange={set('fullName')} placeholder="Your name" />
          </div>
          <div className="field">
            <label>Phone *</label>
            <input
              value={form.phone}
              onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value.replace(/\D/g, '').slice(0, 10) }))}
              placeholder="10-digit number"
              inputMode="numeric"
            />
          </div>
        </div>

        <div className="field">
          <label>Address *</label>
          <input value={form.line1} onChange={set('line1')} placeholder="House / flat, street" />
        </div>
        <div className="field">
          <label>Landmark (optional)</label>
          <input value={form.line2} onChange={set('line2')} placeholder="Nearby landmark" />
        </div>

        <div className="form-row">
          <div className="field">
            <label>Pincode *</label>
            <input value={form.pincode} onChange={onPincode} placeholder="6-digit pincode" inputMode="numeric" />
          </div>
          <div className="field">
            <label>City / District</label>
            <input value={form.city} onChange={set('city')} placeholder="Auto-filled from pincode" />
          </div>
        </div>

        <div className="field">
          <label>State</label>
          <input value={form.state} onChange={set('state')} placeholder="Auto-filled from pincode" />
        </div>

        <div className="field">
          <label>Address Type</label>
          <div className="type-chips">
            {PRESET_TYPES.map((t) => (
              <button
                key={t.id}
                type="button"
                className={form.type === t.id ? 'active' : ''}
                onClick={() => { setCustom(false); setForm((f) => ({ ...f, type: t.id })); }}
              >
                <i className={`fas ${t.icon}`} /> {t.label}
              </button>
            ))}
            <button
              type="button"
              className={custom ? 'active' : ''}
              onClick={() => { setCustom(true); setForm((f) => ({ ...f, type: '' })); }}
            >
              <i className="fas fa-pen" /> Custom
            </button>
          </div>

          {custom ? (
            <input
              style={{ marginTop: 10 }}
              value={form.type}
              onChange={(e) => setForm((f) => ({ ...f, type: e.target.value.slice(0, 20) }))}
              placeholder="e.g. Mom's House, Shop, Farm"
              autoFocus
            />
          ) : null}
        </div>

        <label className="check-row">
          <input
            type="checkbox"
            checked={!!form.isDefault}
            onChange={(e) => setForm((f) => ({ ...f, isDefault: e.target.checked }))}
          />
          Make this my default address
        </label>

        {err ? <p className="form-msg err">{err}</p> : null}

        <button className="consult-btn" style={{ width: '100%', justifyContent: 'center' }} disabled={busy} onClick={save}>
          {busy ? 'Saving…' : 'Save Address'}
        </button>
      </div>
    </div>
  );
}
